import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

// Skip strict validation during Trigger.dev builds (indexing phase) AND runtime
// During BUILD: TRIGGER_SECRET_KEY is set, VERCEL is not '1'
// During RUNTIME: TRIGGER_SECRET_KEY is set, we're running tasks
const isTriggerBuild = 
  process.env.TRIGGER_SECRET_KEY !== undefined && 
  process.env.VERCEL !== '1';

// CRITICAL: Also detect Trigger.dev RUNTIME environment
// When tasks are executing, we're in a Trigger.dev container, not Vercel
const isTriggerRuntime = 
  process.env.TRIGGER_SECRET_KEY !== undefined ||
  process.env.TRIGGER_API_URL !== undefined;

// Also skip if explicitly requested
const skipValidation = isTriggerBuild || isTriggerRuntime || process.env.SKIP_ENV_VALIDATION === '1';

if (skipValidation) {
  console.log('⚠️ Skipping environment validation (Trigger.dev detected)');
  console.log('   TRIGGER_SECRET_KEY:', process.env.TRIGGER_SECRET_KEY ? 'present' : 'missing');
  console.log('   TRIGGER_API_URL:', process.env.TRIGGER_API_URL ? 'present' : 'missing');
  console.log('   VERCEL:', process.env.VERCEL);
  console.log('   SKIP_ENV_VALIDATION:', process.env.SKIP_ENV_VALIDATION);
  console.log('   NODE_ENV:', process.env.NODE_ENV);
}

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  skipValidation,
  server: {
    POSTGRES_PRISMA_URL: skipValidation 
      ? z.string().optional()
      : process.env.NODE_ENV === "production"
        ? z.string().url()
        : z.string().optional(),
    POSTGRES_URL_NON_POOLING: skipValidation
      ? z.string().optional()
      : process.env.NODE_ENV === "production"
        ? z.string().url()
        : z.string().optional(),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),

    RESEND_API_KEY: z.string().optional(),
    LOOPS_API_KEY: z.string().optional(),

    TRIGGER_SECRET_KEY: z.string().optional(),
    TRIGGER_API_URL: z.string().optional(),

    OPENAI_API_KEY: z.string().optional(),
    DEEPSEEK_API_KEY: z.string().optional(),
    GROQ_API_KEY: z.string().optional(),
    ANTHROPIC_API_KEY: z.string().optional(),

    CLERK_SECRET_KEY: z.string().optional(),
    CLERK_WH_SECRET: z.string().optional(),
    /** Host Clerk org for Inbound GPT builder / self-serve portal (optional). */
    CLERK_INBOUND_GPT_ORGANIZATION_ID: z.string().optional(),
    /** Host Clerk org for partner landing / co-branded portals (optional). */
    CLERK_PARTNER_PORTAL_ORGANIZATION_ID: z.string().optional(),
    /** SiteGlue AI company org on app.easygpt.builders (optional; siteglue.ai signups). */
    CLERK_SITEGLUE_ORGANIZATION_ID: z.string().optional(),
    /** Amplify360 brand org for org-scoped marketing admin (optional). */
    CLERK_AMPLIFY360_ORGANIZATION_ID: z.string().optional(),
    /** Service Clerk user ID for marketplace hire clones (optional; falls back to caller). */
    MARKETPLACE_HIRE_SERVICE_CLERK_USER_ID: z.string().optional(),
    
    // Multi-brand Clerk support
    CLERK_SECRET_KEY_EASYGPT: z.string().optional(),
    CLERK_SECRET_KEY_PRACTICECLAW: z.string().optional(),

    UPSTASH_REDIS_REST_URL: z.string().optional(),
    UPSTASH_REDIS_REST_TOKEN: z.string().optional(),

    BLOB_READ_WRITE_TOKEN: z.string().optional(),

    STRIPE_PUBLISHABLE_KEY: z.string().optional(),
    STRIPE_SECRET_KEY: z.string().optional(),
    STRIPE_WEBHOOK_SECRET: z.string().optional(),
    /** Brand-scoped Stripe keys (Clerk org brands on shared app.easygpt.builders deployment). */
    STRIPE_PUBLISHABLE_KEY_EASYGPT: z.string().optional(),
    STRIPE_SECRET_KEY_EASYGPT: z.string().optional(),
    STRIPE_WEBHOOK_SECRET_EASYGPT: z.string().optional(),
    STRIPE_PUBLISHABLE_KEY_SITEGLUE: z.string().optional(),
    STRIPE_SECRET_KEY_SITEGLUE: z.string().optional(),
    STRIPE_WEBHOOK_SECRET_SITEGLUE: z.string().optional(),
    STRIPE_PUBLISHABLE_KEY_AMPLIFY360: z.string().optional(),
    STRIPE_SECRET_KEY_AMPLIFY360: z.string().optional(),
    STRIPE_WEBHOOK_SECRET_AMPLIFY360: z.string().optional(),
    SCRAPER_API_KEY: z.string().optional(),

    IP_2_LOCATION_API_KEY: z.string().optional(),

    // Web Push / VAPID keys for push notifications
    VAPID_PUBLIC_KEY: z.string().optional(),
    VAPID_PRIVATE_KEY: z.string().optional(),
    VAPID_SUBJECT: z.string().optional(), // Usually your app's URL or email

    AGENTMAIL_API_KEY: z.string().optional(),
    AGENTMAIL_WEBHOOK_SECRET: z.string().optional(),

    /** AgentPhone — generic fallback; brand-specific keys override on each deployment. */
    AGENTPHONE_API_KEY: z.string().optional(),
    /** EasyGPT (Jim Wanek / jwanek@easygpt.builders) — approved 10DLC campaign. */
    AGENTPHONE_API_KEY_EASYGPT: z.string().optional(),
    /** PracticeClaw AgentPhone account. */
    AGENTPHONE_API_KEY_PRACTICECLAW: z.string().optional(),

    /** Anam AI — server-side API key for minting browser session tokens. */
    ANAM_API_KEY: z.string().optional(),

    /** Bearer secret for POST /api/lightweight-orchestrator/dispatch (staging/QA). */
    LIGHTWEIGHT_ORCHESTRATOR_DISPATCH_SECRET: z.string().optional(),

    /** Pipedream Connect (workspace OAuth client + Connect project) — optional; enables MCP Servers → Pipedream catalog. */
    PIPEDREAM_CLIENT_ID: z.string().optional(),
    PIPEDREAM_CLIENT_SECRET: z.string().optional(),
    PIPEDREAM_PROJECT_ID: z.string().optional(),
    /** `development` or `production` (default development). */
    PIPEDREAM_PROJECT_ENVIRONMENT: z.string().optional(),

    /** Platform-wide Meta Messenger app (one Developer app for all EasyGPT customers). */
    MESSENGER_APP_ID: z.string().optional(),
    MESSENGER_APP_SECRET: z.string().optional(),
    MESSENGER_WEBHOOK_VERIFY_TOKEN: z.string().optional(),
    /** Override OAuth callback URL if needed (default: NEXT_PUBLIC_APP_URL + /api/messenger/oauth/callback). */
    MESSENGER_OAUTH_REDIRECT_URI: z.string().optional(),
    /** Facebook Login for Business configuration ID (Business-type Meta apps). */
    MESSENGER_FACEBOOK_LOGIN_CONFIG_ID: z.string().optional(),

    /** Admin marketing — Facebook Page OAuth (/admin/integrations). */
    FACEBOOK_APP_ID: z.string().optional(),
    FACEBOOK_APP_SECRET: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_DEPLOYMENT_URL: z.string().optional(),
    NEXT_PUBLIC_TRIGGER_PUBLIC_API_KEY: z.string().optional(),

    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.string().optional(),
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: z.string().optional(),
    /** Comma-separated origins for ClerkProvider.allowedRedirectOrigins (staging / multi-host). */
    NEXT_PUBLIC_CLERK_ALLOWED_REDIRECT_ORIGINS: z.string().optional(),
    /** Client-readable SiteGlue AI org id (mirror CLERK_SITEGLUE_ORGANIZATION_ID). */
    NEXT_PUBLIC_CLERK_SITEGLUE_ORGANIZATION_ID: z.string().optional(),
    
    // Multi-brand Clerk support
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_EASYGPT: z.string().optional(),
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PRACTICECLAW: z.string().optional(),

    NEXT_PUBLIC_PLAUSIBLE_SELFHOSTED_URL: z.string().optional(),

    NEXT_PUBLIC_LOGSNAG_TOKEN: z.string().optional(),
    NEXT_PUBLIC_LOGSNAG_PROJECT: z.string().optional(),
    NEXT_PUBLIC_LOGSNAG_DISABLE_TRACKING: z.string().optional(),

    // VAPID public key (needed on client for subscription)
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: z.string().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    POSTGRES_PRISMA_URL: process.env.POSTGRES_PRISMA_URL,
    POSTGRES_URL_NON_POOLING: process.env.POSTGRES_URL_NON_POOLING,
    NODE_ENV: process.env.NODE_ENV,
    TRIGGER_SECRET_KEY: process.env.TRIGGER_API_KEY,
    TRIGGER_API_URL: process.env.TRIGGER_API_URL,

    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    DEEPSEEK_API_KEY: process.env.DEEPSEEK_API_KEY,
    NEXT_PUBLIC_DEPLOYMENT_URL: process.env.NEXT_PUBLIC_DEPLOYMENT_URL,
    NEXT_PUBLIC_PLAUSIBLE_SELFHOSTED_URL:
      process.env.NEXT_PUBLIC_PLAUSIBLE_SELFHOSTED_URL,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    LOOPS_API_KEY: process.env.LOOPS_API_KEY,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL,
    NEXT_PUBLIC_CLERK_ALLOWED_REDIRECT_ORIGINS:
      process.env.NEXT_PUBLIC_CLERK_ALLOWED_REDIRECT_ORIGINS,
    NEXT_PUBLIC_CLERK_SITEGLUE_ORGANIZATION_ID:
      process.env.NEXT_PUBLIC_CLERK_SITEGLUE_ORGANIZATION_ID,
    NEXT_PUBLIC_TRIGGER_PUBLIC_API_KEY:
      process.env.NEXT_PUBLIC_TRIGGER_PUBLIC_API_KEY,
    CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
    CLERK_WH_SECRET: process.env.CLERK_WH_SECRET,
    CLERK_INBOUND_GPT_ORGANIZATION_ID: process.env.CLERK_INBOUND_GPT_ORGANIZATION_ID,
    CLERK_PARTNER_PORTAL_ORGANIZATION_ID: process.env.CLERK_PARTNER_PORTAL_ORGANIZATION_ID,
    CLERK_SITEGLUE_ORGANIZATION_ID: process.env.CLERK_SITEGLUE_ORGANIZATION_ID,
    MARKETPLACE_HIRE_SERVICE_CLERK_USER_ID: process.env.MARKETPLACE_HIRE_SERVICE_CLERK_USER_ID,
    CLERK_SECRET_KEY_EASYGPT: process.env.CLERK_SECRET_KEY_EASYGPT,
    CLERK_SECRET_KEY_PRACTICECLAW: process.env.CLERK_SECRET_KEY_PRACTICECLAW,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_EASYGPT: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_EASYGPT,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PRACTICECLAW: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY_PRACTICECLAW,
    NEXT_PUBLIC_LOGSNAG_TOKEN: process.env.NEXT_PUBLIC_LOGSNAG_TOKEN,
    NEXT_PUBLIC_LOGSNAG_PROJECT: process.env.NEXT_PUBLIC_LOGSNAG_PROJECT,
    NEXT_PUBLIC_LOGSNAG_DISABLE_TRACKING:
      process.env.NEXT_PUBLIC_LOGSNAG_DISABLE_TRACKING,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    BLOB_READ_WRITE_TOKEN: process.env.BLOB_READ_WRITE_TOKEN,
    STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_PUBLISHABLE_KEY_EASYGPT: process.env.STRIPE_PUBLISHABLE_KEY_EASYGPT,
    STRIPE_SECRET_KEY_EASYGPT: process.env.STRIPE_SECRET_KEY_EASYGPT,
    STRIPE_WEBHOOK_SECRET_EASYGPT: process.env.STRIPE_WEBHOOK_SECRET_EASYGPT,
    STRIPE_PUBLISHABLE_KEY_SITEGLUE: process.env.STRIPE_PUBLISHABLE_KEY_SITEGLUE,
    STRIPE_SECRET_KEY_SITEGLUE: process.env.STRIPE_SECRET_KEY_SITEGLUE,
    STRIPE_WEBHOOK_SECRET_SITEGLUE: process.env.STRIPE_WEBHOOK_SECRET_SITEGLUE,
    STRIPE_PUBLISHABLE_KEY_AMPLIFY360: process.env.STRIPE_PUBLISHABLE_KEY_AMPLIFY360,
    STRIPE_SECRET_KEY_AMPLIFY360: process.env.STRIPE_SECRET_KEY_AMPLIFY360,
    STRIPE_WEBHOOK_SECRET_AMPLIFY360: process.env.STRIPE_WEBHOOK_SECRET_AMPLIFY360,
    SCRAPER_API_KEY: process.env.SCRAPER_API_KEY,
    IP_2_LOCATION_API_KEY: process.env.IP_2_LOCATION_API_KEY,
    NEXT_PUBLIC_VAPID_PUBLIC_KEY: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
    VAPID_PUBLIC_KEY: process.env.VAPID_PUBLIC_KEY,
    VAPID_PRIVATE_KEY: process.env.VAPID_PRIVATE_KEY,
    VAPID_SUBJECT: process.env.VAPID_SUBJECT || process.env.NEXT_PUBLIC_DEPLOYMENT_URL,
    AGENTMAIL_API_KEY: process.env.AGENTMAIL_API_KEY,
    AGENTMAIL_WEBHOOK_SECRET: process.env.AGENTMAIL_WEBHOOK_SECRET,
    AGENTPHONE_API_KEY: process.env.AGENTPHONE_API_KEY,
    AGENTPHONE_API_KEY_EASYGPT: process.env.AGENTPHONE_API_KEY_EASYGPT,
    AGENTPHONE_API_KEY_PRACTICECLAW: process.env.AGENTPHONE_API_KEY_PRACTICECLAW,
    ANAM_API_KEY: process.env.ANAM_API_KEY,
    LIGHTWEIGHT_ORCHESTRATOR_DISPATCH_SECRET:
      process.env.LIGHTWEIGHT_ORCHESTRATOR_DISPATCH_SECRET,
    PIPEDREAM_CLIENT_ID: process.env.PIPEDREAM_CLIENT_ID,
    PIPEDREAM_CLIENT_SECRET: process.env.PIPEDREAM_CLIENT_SECRET,
    PIPEDREAM_PROJECT_ID: process.env.PIPEDREAM_PROJECT_ID,
    PIPEDREAM_PROJECT_ENVIRONMENT: process.env.PIPEDREAM_PROJECT_ENVIRONMENT,
    MESSENGER_APP_ID: process.env.MESSENGER_APP_ID,
    MESSENGER_APP_SECRET: process.env.MESSENGER_APP_SECRET,
    MESSENGER_WEBHOOK_VERIFY_TOKEN: process.env.MESSENGER_WEBHOOK_VERIFY_TOKEN,
    MESSENGER_OAUTH_REDIRECT_URI: process.env.MESSENGER_OAUTH_REDIRECT_URI,
    MESSENGER_FACEBOOK_LOGIN_CONFIG_ID: process.env.MESSENGER_FACEBOOK_LOGIN_CONFIG_ID,
    FACEBOOK_APP_ID: process.env.FACEBOOK_APP_ID,
    FACEBOOK_APP_SECRET: process.env.FACEBOOK_APP_SECRET,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
